import { useCallback, useMemo } from 'react'
import { Box, Stack } from '@mui/material'

import AudioPlayer from '@/apps/latest/components/molecules/AudioPlayer'
import { HighlightList } from '@/apps/latest/components/molecules/HighlightList'
import { SectionHeading } from '@/components/atoms/SectionHeading'
import { BodyText } from '@/components/atoms/BodyText'
import { MetaText } from '@/components/atoms/MetaText'
import { MemoPanel } from '@/apps/latest/features/yakureki/components/MemoPanel'
import { type YakurekiDetail } from '@/shared/types/yakurekiCore'

import { CopyableHeading } from './CopyableHeading'
import { useCopyText } from './hooks/useSoapCopy'

interface Props {
  event: YakurekiDetail
}

// 会話 (文字起こし) ビュー. 会話の材料を時間順に並べる: メモ (録音中の手書き) → ハイライト (AI の抽出) →
// 発話 (全文) → 音声. 録音前の確認事項 (指導前準備 pane の一覧) はここに再掲しない — 録音後に見る仕事が無く、
// 5 月に旧タブの格下げ先として残っていただけ (hub log/108 決定 44・design-book ADR-0055).
// Notion §4.4 録音後 Summary 内ブロック並列哲学に整合.
export const ConversationView = ({ event }: Props) => {
  // 会話ビューの発話 / ハイライトは成果物でなく参照素材なので、 完了させない系統2 を使う.
  const copyText = useCopyText()
  const highlights = useMemo(() => event.highlights ?? [], [event.highlights])
  const conversationSegments = useMemo(
    () => event.dictated?.conversation_segments ?? [],
    [event.dictated]
  )

  const handleCopyHighlights = useCallback(() => {
    const text = highlights.map((h) => `・${h.text}`).join('\n')
    void copyText(text, 'ハイライトをコピーしました')
  }, [highlights, copyText])

  // 発話 (全文) コピー. ハイライトと同じく「見出しクリック = そのブロックをコピー」に
  // 統一する (= 会話ビューのヘッダーコピーアイコンは廃止. SOAP 本体と同じ Notion 流).
  const handleCopySegments = useCallback(() => {
    const text = conversationSegments
      .map((s) => `${s.speaker}: ${s.content}`)
      .join('\n')
    void copyText(text, '発話をコピーしました')
  }, [conversationSegments, copyText])

  return (
    <Stack spacing={2}>
      <Box>
        <SectionHeading>メモ</SectionHeading>
        <Box sx={{ mt: 0.5 }}>
          <MemoPanel yakurekiId={event.id} memoItems={event.memo_items ?? []} />
        </Box>
      </Box>
      <Box>
        {highlights.length > 0 ? (
          <CopyableHeading
            label="ハイライト"
            title="ハイライトをコピー"
            onCopy={() => void handleCopyHighlights()}
          />
        ) : (
          <SectionHeading>ハイライト</SectionHeading>
        )}
        <Box sx={{ mt: 0.5 }}>
          <HighlightList highlights={highlights} order="asc" />
        </Box>
      </Box>
      {conversationSegments.length > 0 ? (
        <Box>
          <CopyableHeading
            label="発話"
            title="発話をコピー"
            onCopy={() => void handleCopySegments()}
          />
          {/* 発話 = 全文の生記録. 話者を左 gutter (MetaText)、 本文を右に baseline
              揃えで dense に並べる (= 抽出済みハイライトより軽い参照). */}
          <Stack spacing={0.5} sx={{ mt: 0.5 }}>
            {conversationSegments.map((seg, i) => (
              <Box
                key={i}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '80px 1fr',
                  gap: 1,
                  alignItems: 'baseline',
                }}
              >
                <MetaText>{seg.speaker}</MetaText>
                <BodyText sx={{ whiteSpace: 'pre-wrap' }}>
                  {seg.content}
                </BodyText>
              </Box>
            ))}
          </Stack>
        </Box>
      ) : (
        <MetaText sx={{ py: 2, textAlign: 'center' }}>
          発話の記録がまだありません
        </MetaText>
      )}
      {event.audio?.audio && (
        <Box sx={{ mt: 1 }}>
          <AudioPlayer audioUrl={event.audio.audio} />
        </Box>
      )}
    </Stack>
  )
}
